import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useAuthModal } from '../contexts/AuthModalContext';
import { Button, OroLoader } from '../components';
import PublicHeader from '../components/PublicHeader';
import StaticFooter from '../components/StaticFooter';
import AppStoreBadge from '../components/AppStoreBadge';
import OroTrademark from '../components/OroTrademark';
import EnvironmentBanner from '../components/EnvironmentBanner';
import Reveal from '../components/marketing/Reveal';
import SectionHead from '../components/marketing/SectionHead';
import MarketingSection from '../components/marketing/MarketingSection';
import { MEMBERS } from '../data/content/members';
import { PILLAR_ICONS, splitEmphasis } from './membersPage.helpers';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { MARKETING_PAGE_META } from '../lib/seo/pageMeta';

/**
 * "For members" — the argument page (#3218, the fifth audience page of #3122).
 *
 * The sister of `/leaders` (#3125): same shared kit (MarketingSection,
 * SectionHead, Reveal, PublicHeader, StaticFooter, OroTrademark), same anchor
 * ("Ten minutes before", #3122), a different reader. `/leaders` argues to the
 * organiser; this page argues to the PARTICIPANT — the person handed an invite
 * by their club, deciding whether to show up. Boundary and gate record live on
 * #3218 (A0, Gate 1/2/3, B3 identity swap, B4 motion).
 *
 *  - COPY lives in `data/content/members.ts` (check-voice scans it; the gallery
 *    round-trip edits it). Do not inline strings here (#2954).
 *  - ™ CADENCE (Core Rule 24): exactly ONE mark, in the hero via OroTrademark.
 *    Gold is spent twice AT REST — that mark and the single close CTA (#3122
 *    anchor). Hover accents (the hero ghost control, the /leaders link) go gold
 *    on hover, following the site convention, exactly as /leaders does. The ™
 *    count is pinned by MembersPage.test.tsx.
 *  - MOTION (Gate 3 / B4): section heads fade up on entry via the kit's Reveal,
 *    ONE theme only — no staggered lines, nothing else moves. Reduced-motion
 *    renders everything visible from first paint (Reveal handles #3174).
 *  - Authed visitors redirect to /home, same as the front door and /leaders —
 *    this is a prospect surface, logged-out-only.
 *
 * HERO IMAGE: owner-sourced Unsplash frame, chosen 2026-09-10 for the
 * "You're invited" headline — four silhouettes mid-jump against a sunset, no
 * legible faces (Shahir Shah, unsplash.com/photos/Z4GiC4acBb8; provenance and
 * the deliberate departure from the #3122 "ten minutes before" arrival anchor
 * recorded on #3218). Self-hosted at 2400px under /images/ like the /leaders
 * frame; `MembersPage.test.tsx` asserts the file exists on disk.
 */
const HERO_IMAGE = '/images/shahir-shah-Z4GiC4acBb8-unsplash.jpg';

/** Reveal only once the head is ~30% up from the fold, so a lower section does
 *  not animate while the visitor is still on the one above it (see /leaders). */
const REVEAL_MARGIN = '0px 0px -30% 0px';

/** The hero's ghost control scrolls here (S2). One constant so the link and the
 *  section id cannot drift apart. */
const S2_ID = 'the-reality';

/** S1 — hero. Name the reader (invited by your club) and the calm promise. The
 *  ™ lives here, on the brand's most prominent use (Rule 24). */
function Hero() {
  const c = MEMBERS.hero;
  return (
    <section className="relative flex items-end overflow-hidden min-h-[min(88vh,48rem)] pb-18">
      <div
        className="members-hero-scrim absolute inset-0 z-0 bg-cover"
        style={{ backgroundImage: `url('${HERO_IMAGE}')`, backgroundPosition: '50% 32%' }}
        aria-hidden="true"
      />
      <div className="relative z-10 w-full max-w-5xl mx-auto px-6">
        <div className="max-w-xl">
          <h1 className="text-hero font-bold leading-[1.04] tracking-tight text-text-primary">
            {c.headline}
          </h1>
          <p className="mt-5 max-w-lg text-base text-text-secondary">{c.sub1}</p>
          <p className="mt-2 max-w-lg text-base text-text-secondary">
            {c.brandLead} <OroTrademark /> {c.brandRest}
          </p>
          <p className="mt-2 max-w-lg text-base text-text-secondary">{c.sub3}</p>
          <p className="mt-7 text-xl font-bold text-text-primary">{c.showUp}</p>
          <div className="mt-8">
            <a
              href={`#${S2_ID}`}
              className="inline-flex items-center gap-2 rounded-lg border border-oro-border-hover px-6 py-3 text-sm font-semibold text-text-primary hover:border-oro-gold hover:text-oro-gold oro-transition"
            >
              {c.cta}
              <ArrowDown className="w-4 h-4" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/** S2 — the reality. Recognition before solution; no product in this section. */
function RealitySection() {
  const c = MEMBERS.reality;
  return (
    <MarketingSection id={S2_ID}>
      <Reveal rootMargin={REVEAL_MARGIN}>
        <SectionHead eyebrow={c.eyebrow} title={c.title} />
      </Reveal>
      <div className="mt-8 max-w-2xl">
        {c.questions.map((q, i) => (
          <p key={i} className="mb-3 text-lg text-text-primary sm:text-xl">
            {q.text}
          </p>
        ))}
        <p className="mt-6 text-text-tertiary">{c.newLead}</p>
        <p className="mt-2 text-xl font-bold text-text-primary sm:text-2xl">{c.newPunch}</p>
        <p className="mt-6 text-text-tertiary">{c.turn}</p>
        <p className="mt-1 text-lg text-text-secondary">{c.tail}</p>
      </div>
    </MarketingSection>
  );
}

/** S3 — three things showing up gets you. Cards with Lucide marks (Gate 2:
 *  three cards, not stacked prose). Each grounded in a shipped capability. */
function PillarsSection() {
  const c = MEMBERS.pillars;
  return (
    <MarketingSection divider>
      <Reveal rootMargin={REVEAL_MARGIN}>
        <SectionHead eyebrow={c.eyebrow} title={c.title} />
      </Reveal>
      <div className="mt-11 grid gap-5 md:grid-cols-3">
        {c.items.map((item) => {
          const Icon = PILLAR_ICONS[item.id];
          return (
            <div
              key={item.id}
              className="rounded-xl border border-oro-border bg-oro-surface p-6"
            >
              {Icon && (
                <span className="mb-4 grid h-9 w-9 place-items-center rounded-md border border-oro-border-hover text-oro-club">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
              )}
              <h3 className="mb-2.5 text-lg font-semibold text-text-primary">{item.heading}</h3>
              {item.body.map((p, j) => (
                <p key={j} className="mb-2.5 text-sm text-text-secondary">
                  {p.text}
                </p>
              ))}
              {'strong' in item && (
                <p className="mt-3 text-sm font-bold text-text-primary">{item.strong}</p>
              )}
            </div>
          );
        })}
      </div>
    </MarketingSection>
  );
}

/** One timeline line. `emphasis` bolds a single word inside `text`; `tone`
 *  styles the whole line. Typed with optional props so the `as const` union of
 *  line shapes assigns cleanly. */
function TimelineLine({ line }: { line: { text: string; emphasis?: string; tone?: string } }) {
  if (line.tone === 'sub') {
    return <p className="text-sm text-text-muted">{line.text}</p>;
  }
  if (line.tone === 'msg') {
    return (
      <p className="my-2 border-l-2 border-oro-border-hover pl-3 italic text-text-secondary">
        {line.text}
      </p>
    );
  }
  if (line.tone === 'punch') {
    return <p className="mt-1 text-base font-bold text-text-primary">{line.text}</p>;
  }
  if (line.emphasis) {
    const parts = splitEmphasis(line.text, line.emphasis);
    if (parts) {
      return (
        <p className="text-base text-text-primary">
          {parts.before}
          <strong className="font-semibold">{parts.match}</strong>
          {parts.after}
        </p>
      );
    }
  }
  return <p className="text-base text-text-primary">{line.text}</p>;
}

/** S4 — one Saturday, from your side. Seeded Golden Hour data as a timeline
 *  (the 9rem day column, like /leaders' WeekSection), never the front door's
 *  demo cards. */
function SaturdaySection() {
  const c = MEMBERS.saturday;
  return (
    <MarketingSection divider>
      <Reveal rootMargin={REVEAL_MARGIN}>
        <SectionHead eyebrow={c.eyebrow} title={c.title} />
      </Reveal>
      <div className="mt-10 max-w-3xl">
        {c.steps.map((step, i) => (
          <div
            key={step.when}
            className={`grid grid-cols-1 gap-1 py-4 sm:grid-cols-[9rem_1fr] sm:gap-6${
              i === 0 ? '' : ' border-t border-oro-border'
            }`}
          >
            <span className="text-sm font-semibold text-oro-club">{step.when}</span>
            <div>
              {step.lines.map((line, j) => (
                <TimelineLine key={j} line={line} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </MarketingSection>
  );
}

/** S5 — what it never asks of you. Every line a true negative today (ADR-0019). */
function LeaveOutSection() {
  const c = MEMBERS.leaveOut;
  return (
    <MarketingSection divider>
      <Reveal rootMargin={REVEAL_MARGIN}>
        <SectionHead eyebrow={c.eyebrow} title={c.title} />
      </Reveal>
      <div className="mt-8 max-w-2xl">
        {c.lines.map((l, i) => (
          <p key={i} className="mb-3 text-lg text-text-secondary">
            {l.text}
          </p>
        ))}
        {c.close.map((l, i) => (
          <p key={i} className={`${i === 0 ? 'mt-6' : 'mt-2'} text-text-primary`}>
            {l.text}
          </p>
        ))}
        <p className="mt-6 text-xl font-bold text-text-primary sm:text-2xl">{c.together}</p>
      </div>
    </MarketingSection>
  );
}

/** S6 — how you get in, and the other door. Boundary with /leaders; no
 *  club-discovery claim. The leaders link carries club-blue (not gold). */
function DoorSection() {
  const c = MEMBERS.door;
  return (
    <MarketingSection divider>
      <Reveal rootMargin={REVEAL_MARGIN}>
        <SectionHead eyebrow={c.eyebrow} title={c.title} />
      </Reveal>
      <div className="mt-8 grid gap-8 md:grid-cols-[1.2fr_1fr] md:items-start">
        <div>
          {c.lines.map((l, i) => (
            <p key={i} className="mb-3 text-text-secondary">
              {l.text}
            </p>
          ))}
        </div>
        <div className="rounded-xl border border-oro-border bg-oro-surface p-6">
          <p className="font-semibold text-text-primary">{c.otherLead}</p>
          <p className="mt-1 text-text-secondary">{c.otherBody}</p>
          <Link
            to="/leaders"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-oro-club hover:text-oro-gold oro-transition"
          >
            {c.otherLink}
            <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </MarketingSection>
  );
}

/** S7 — close. Restate the calm promise. Primary gold action opens the sign-in
 *  modal (front-door parity). The quiet secondary link shares the /leaders page:
 *  there is no invite to route to, so a reader without a club hands it to whoever
 *  runs theirs (owner decision 2026-09-10). Share follows the app's ShareModal
 *  pattern — native share when available, clipboard + Sonner toast otherwise. */
function CloseSection() {
  const { openLoginModal } = useAuthModal();
  const c = MEMBERS.close;

  const shareLeaders = async () => {
    const url = `${window.location.origin}/leaders`;
    if (navigator.share) {
      try {
        await navigator.share({ title: c.shareTitle, text: c.shareText, url });
        return;
      } catch (err) {
        // AbortError = the user dismissed the share sheet: do nothing. Any OTHER
        // rejection (desktop Chrome exposes `share` and then rejects) falls
        // through to the clipboard path below — unlike ShareModal, which
        // swallows every rejection because it offers a separate copy button.
        if ((err as { name?: string })?.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(c.copiedToast);
    } catch {
      toast.error(c.copyFailedToast);
    }
  };

  return (
    <MarketingSection divider spacing="cta" align="center">
      <Reveal rootMargin={REVEAL_MARGIN}>
        <h2 className="text-section font-bold tracking-tight text-text-primary">{c.title}</h2>
      </Reveal>
      <p className="mx-auto mt-4 max-w-md text-text-secondary">{c.body1}</p>
      <p className="mx-auto max-w-md text-text-secondary">{c.body2}</p>
      <div className="mt-8 flex flex-col items-center gap-4">
        <Button variant="primary" size="lg" className="py-3 px-8" onClick={() => openLoginModal()}>
          {c.cta}
        </Button>
        <button
          type="button"
          onClick={shareLeaders}
          className="inline-flex items-center min-h-11 px-2 text-sm font-semibold text-oro-club hover:text-oro-gold oro-transition"
        >
          {c.secondary}
        </button>
        {/* #3448: the App Store note + badge under the close CTA. The note is
            copy (voice-gated in members.ts); the badge is Apple's image asset,
            so it adds no ™ to this page's single mark (the hero). */}
        <div className="mt-2 flex flex-col items-center gap-3">
          <p className="text-sm text-text-muted">{c.appStoreNote}</p>
          <AppStoreBadge />
        </div>
      </div>
    </MarketingSection>
  );
}

function MembersPageContent() {
  return (
    <div className="min-h-screen bg-oro-bg text-text-primary flex flex-col">
      <EnvironmentBanner />
      <PublicHeader />
      <main className="flex-1">
        <Hero />
        <RealitySection />
        <PillarsSection />
        <SaturdaySection />
        <LeaveOutSection />
        <DoorSection />
        <CloseSection />
      </main>
      <StaticFooter />
    </div>
  );
}

export default function MembersPage() {
  useDocumentTitle(MARKETING_PAGE_META['/members'].title, { suffix: false });
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      // `replace` so Back does not return to /members and loop the redirect.
      navigate('/home', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Gate the render on isAuthenticated too, not just authLoading: without it an
  // authenticated visitor paints the full logged-out page for one frame before
  // the effect redirects (George M1). Render the loader until the redirect lands.
  if (authLoading || isAuthenticated) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center">
        <OroLoader label="Loading" size={72} />
      </div>
    );
  }

  return <MembersPageContent />;
}
